import type { App } from '../../app.js';
import { h, button } from '../dom.js';
import { ticksToTimeString } from '@voidline/shared';
import { championshipSeries, startChampionshipRace } from './championship.js';

export function buildResultsScreen(app: App): HTMLElement {
  const results = app.lastResults ?? [];
  const online = !!app.raceCtx?.online;
  const localName = app.settings.playerName || 'YOU';

  // championship continuation
  let champNext: (() => void) | null = null;
  const tag = app.raceCtx?.tag;
  if (tag?.startsWith('champ:')) {
    const [, seriesId] = tag.split(':');
    const series = championshipSeries().find((s) => s.id === seriesId);
    const prog = app.progression.championships[seriesId];
    if (series && prog && !prog.done && prog.stage < series.races.length) {
      champNext = () => startChampionshipRace(app, series, prog.stage);
    }
  }

  const rows = results.map((r) => {
    const isMe = online
      ? r.shipIndex === app.driver?.view.localIndex
      : r.shipIndex === 0;
    const medal = r.placement === 1 ? '🏆 ' : r.placement === 2 ? '🥈 ' : r.placement === 3 ? '🥉 ' : '';
    return h(
      'div',
      {
        class: 'row',
        style: `justify-content: space-between; padding: 9px 16px; gap: 20px;
                border-bottom: 1px solid var(--line);
                ${isMe ? 'background: rgba(49,200,255,0.08); border-left: 3px solid var(--accent);' : ''}`,
      },
      h('div', { class: 'row', style: 'gap: 14px;' },
        h('span', {
          class: 'mono',
          style: `width: 2ch; color: ${r.placement <= 3 ? 'var(--gold)' : 'var(--text-dim)'};`,
          text: String(r.placement),
        }),
        h('span', { text: `${medal}${r.name}${isMe && r.name !== localName ? ' (you)' : ''}` }),
        r.isBot ? h('span', { class: 'subtle', style: 'font-size: 10px;', text: 'BOT' }) : null
      ),
      h('div', { class: 'row mono', style: 'gap: 22px; font-size: 13px;' },
        h('span', { class: 'subtle', text: r.bestLapTicks > 0 ? `best ${ticksToTimeString(r.bestLapTicks)}` : '' }),
        h('span', {
          style: r.dnf ? 'color: var(--warn);' : '',
          text: r.dnf ? (r.eliminated ? 'ELIMINATED' : 'DNF') : ticksToTimeString(r.finishTick),
        })
      )
    );
  });

  return h(
    'div',
    { class: 'screen', style: 'justify-content: flex-end; padding-bottom: 5vh; background: linear-gradient(transparent 30%, rgba(4,6,10,0.88) 75%);' },
    h(
      'div',
      { class: 'panel col', style: 'width: min(640px, 94vw); gap: 4px; max-height: 70vh; overflow-y: auto;' },
      h('div', { class: 'h1', text: 'RACE RESULTS', style: 'margin-bottom: 10px;' }),
      ...rows,
      h(
        'div',
        { class: 'row', style: 'margin-top: 18px; gap: 12px;' },
        champNext
          ? button('NEXT RACE ►', champNext, 'primary focusable')
          : online
            ? button('REMATCH (READY UP)', () => app.finishRace('rematch'), 'primary focusable')
            : button('REMATCH', () => app.finishRace('rematch'), 'primary focusable'),
        online ? button('BACK TO LOBBY', () => app.finishRace('lobby'), 'focusable') : null,
        champNext
          ? button('CHAMPIONSHIP', () => {
              app.finishRace('menu');
              app.show('championship');
            }, 'focusable')
          : null,
        button(online ? 'LEAVE' : 'MENU', () => app.finishRace('menu'), 'focusable')
      )
    )
  );
}
